import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export const IS_DEVELOPMENT = process.env.NODE_ENV !== "production";
export const IS_PRODUCTION = process.env.NODE_ENV === "production";

function getCdnUrl() {
  let url = "https://duq6bmhfzq92t.cloudfront.net/public-static";
  try {
    const config = useRuntimeConfig();
    if (config?.public?.imageCdnUrl) {
      url = config.public.imageCdnUrl as string;
    }
  } catch (e) { }
  return url;
}

export function getArtworkImageSrc(
  photoName: string,
  quality: 1920 | 720 | 360,
) {
  const cdnUrl = getCdnUrl();
  return `${cdnUrl}/artwork-images/${quality}/${photoName}.webp`;
}

// QAM framing/repair cost as stored (free text from Airtable, e.g. "5500" or "5,500").
// Returns the number when it reads as one, otherwise null.
export function parseFramingCost(raw: unknown): number | null {
  if (raw === undefined || raw === null) return null;
  // allow a leading currency marker, thousands separators and spaces; anything else is not "a number"
  const cleaned = String(raw).trim().replace(/^(?:₹|rs\.?|inr)\s*/i, "").replace(/[,\s]/g, "");
  if (!/^-?\d+(?:\.\d+)?$/.test(cleaned)) return null;
  return Number(cleaned);
}

// Total of several framed pieces' costs - only when EVERY one of them is a number, otherwise null, so a
// total is never shown (or charged) from a partial list.
export function totalFramingCost(raws: unknown[]): number | null {
  if (!raws.length) return null;
  let sum = 0;
  for (const raw of raws) {
    const n = parseFramingCost(raw);
    if (n === null) return null;
    sum += n;
  }
  return sum;
}

// What the "existing frame" dialog shows for a cost: "₹5,500" for a number, the text itself when it
// is not a plain number, and "-" when there is nothing.
export function formatFramingCost(raw: unknown): string {
  const text = raw === undefined || raw === null ? "" : String(raw).trim();
  if (!text) return "-";
  const n = parseFramingCost(text);
  if (n === null) return text;
  return "\u20B9" + n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

// Framed artwork images are 720 webp only, in a flat folder (no quality sub-directory)
export function getFramedArtworkImageSrc(photoName: string) {
  const cdnUrl = getCdnUrl();
  return `${cdnUrl}/framed-artwork-images/${photoName}.webp`;
}

export function getArtworkSetImageSrc(
  photoName: string,
  quality: 1920 | 720 | 360,
) {
  const cdnUrl = getCdnUrl();
  return `${cdnUrl}/artwork-set-images/${quality}/${photoName}.webp`;
}

export function getArtistProfilePhotoSrc(
  photoName: string,
  quality: 1920 | 720 | 360,
) {
  const cdnUrl = getCdnUrl();
  return `${cdnUrl}/artist-images/${quality}/${photoName}.webp`;
}

export function getArtistOfTheDayPhotoSrc(
  photoName: string,
  quality: 1920 | 720 | 360,
) {
  const cdnUrl = getCdnUrl();
  return `${cdnUrl}/artist-of-the-day-images/${quality}/${photoName}.webp`;
}

export function getConstellationImageSrc(
  photoName: string,
) {
  const cdnUrl = getCdnUrl();
  return `${cdnUrl}/constellations-images/${photoName}`;
}

export function getConstellationMomentSrc(
  fileName: string,
) {
  const cdnUrl = getCdnUrl();
  return `${cdnUrl}/constellations-moments/${fileName}`;
}


export function getDashboardImageSrc(
  photoName: string,
  quality: 1920 | 720 | 360,
) {
  const url = getArtworkImageSrc(photoName, quality);
  if (url.includes(".s3.") || url.includes("amazonaws.com")) {
    return `/api/proxy-image?url=${encodeURIComponent(url)}`;
  }
  return url;
}

export function getDashboardSetImageSrc(
  photoName: string,
  quality: 1920 | 720 | 360,
) {
  const url = getArtworkSetImageSrc(photoName, quality);
  if (url.includes(".s3.") || url.includes("amazonaws.com")) {
    return `/api/proxy-image?url=${encodeURIComponent(url)}`;
  }
  return url;
}

export function getDashboardArtistPhotoSrc(
  photoName: string,
  quality: 1920 | 720 | 360,
) {
  const url = getArtistProfilePhotoSrc(photoName, quality);
  if (url.includes(".s3.") || url.includes("amazonaws.com")) {
    return `/api/proxy-image?url=${encodeURIComponent(url)}`;
  }
  return url;
}

// Source - https://stackoverflow.com/a/12646864
// Posted by Laurens Holst, modified by community. See post 'Timeline' for change history
// Retrieved 2026-04-10, License - CC BY-SA 4.0

export function shuffleArray(array: any[]) {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }

  return [...array];
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function convertInchesToCm(sizeStr: string | null | undefined): string {
  if (!sizeStr) return "";
  // Normalize string: remove "in" or "inches", trim whitespace
  const cleanStr = sizeStr.replace(/in(ches)?/gi, "").trim();

  // Find all numbers (including decimals)
  const numbers = cleanStr.match(/\d+(\.\d+)?/g);
  if (!numbers || numbers.length === 0) {
    return sizeStr; // Fallback if no numbers found
  }

  // Replace each number in the cleanStr with its cm equivalent
  const converted = cleanStr.replace(/\d+(\.\d+)?/g, (match) => {
    const inches = parseFloat(match);
    const cm = Math.round(inches * 2.54 * 10) / 10; // Round to 1 decimal place
    return cm.toString();
  });

  return `${converted} cm`;
}
